// src/features/admin/AuditLogDetailRail.tsx
// One admin audit-log entry: actor, action, resource, before/after state.

import { observer } from '@react';
import { useI18n } from '@react/hooks';
import type { AuditLogEntry } from '@core/types/admin';
import { enumKey, fmtDateTime } from './logic';

interface AuditLogDetailRailProps {
  entry: AuditLogEntry;
  onClose: () => void;
}

export const AuditLogDetailRail = observer(({ entry, onClose }: AuditLogDetailRailProps) => {
  const { t, locale } = useI18n();

  return (
    <aside className="w-full shrink-0 space-y-3 overflow-y-auto border-l border-border p-4 lg:w-[26rem]">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">{t('admin.auditLog.detail.title')}</h2>
        <button onClick={onClose} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>

      <dl className="space-y-2 text-sm">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('admin.auditLog.column.actor')}</dt>
          <dd className="text-right text-foreground">{entry.actor?.display_name || entry.actor?.entity?.id || '—'}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('admin.auditLog.column.action')}</dt>
          <dd className="text-right text-foreground">{t(`admin.auditAction.${enumKey(entry.action, 'AUDIT_ACTION_')}`)}</dd>
        </div>
        {entry.action_detail && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('admin.auditLog.detail.actionDetail')}</dt>
            <dd className="text-right text-foreground">{entry.action_detail}</dd>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('admin.auditLog.column.resource')}</dt>
          <dd className="text-right text-foreground">{entry.resource_type}{entry.resource?.id ? ` · ${entry.resource.id}` : ''}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('admin.auditLog.column.scope')}</dt>
          <dd className="text-right text-foreground">{t(`admin.auditScope.${enumKey(entry.scope, 'AUDIT_SCOPE_')}`)}{entry.scope_id ? ` · ${entry.scope_id}` : ''}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('admin.auditLog.column.occurred')}</dt>
          <dd className="text-right text-foreground">{fmtDateTime(entry.occurred_at as unknown as string, locale)}</dd>
        </div>
      </dl>

      {entry.context && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('admin.auditLog.detail.context')}</h3>
          <dl className="mt-1 space-y-1 text-sm">
            {entry.context.request_id && (
              <div className="flex justify-between gap-2"><dt className="text-muted-foreground">{t('admin.auditLog.detail.requestId')}</dt><dd className="text-right text-foreground break-all">{entry.context.request_id}</dd></div>
            )}
            {entry.context.ip_address && (
              <div className="flex justify-between gap-2"><dt className="text-muted-foreground">{t('admin.auditLog.detail.ip')}</dt><dd className="text-right text-foreground">{entry.context.ip_address}</dd></div>
            )}
            {entry.context.service_name && (
              <div className="flex justify-between gap-2"><dt className="text-muted-foreground">{t('admin.auditLog.detail.service')}</dt><dd className="text-right text-foreground">{entry.context.service_name}</dd></div>
            )}
          </dl>
        </div>
      )}

      {(entry.old_value || entry.new_value) && (
        <div className="space-y-2">
          {entry.old_value && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('admin.auditLog.detail.before')}</h3>
              <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-muted p-2 text-xs">{entry.old_value}</pre>
            </div>
          )}
          {entry.new_value && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('admin.auditLog.detail.after')}</h3>
              <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-muted p-2 text-xs">{entry.new_value}</pre>
            </div>
          )}
        </div>
      )}
    </aside>
  );
});

export default AuditLogDetailRail;
